import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CContainer, CButton, CSpinner, CFormSelect } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilArrowLeft,
  cilSave,
  cilMap,
  cilGrid,
  cilReload,
} from "@coreui/icons";
import { toast } from "react-toastify";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  YMaps,
  Map,
  Placemark,
  ZoomControl,
  TypeSelector,
  Clusterer,
} from "@pbe/react-yandex-maps";
import dayjs from "dayjs";
import "./MultiMonitorPage.css";
import i18n from "../../../i18n";

const ResponsiveGridLayout = WidthProvider(Responsive);

const MultiMonitorPage = () => {
  const [devices, setDevices] = useState([]);
  const [layout, setLayout] = useState([]);
  const [isMapMode, setIsMapMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [workZones, setWorkZones] = useState([]);
  const [selectedZoneFilter, setSelectedZoneFilter] = useState("");

  const [gridSize, setGridSize] = useState("all");
  const [gridCols, setGridCols] = useState(12);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const [layoutRes, devRes, zonesRes] = await Promise.all([
          axios.get("/api/user/layout/", { headers }),
          axios.get(`/api/fleet/realtime/?_t=${Date.now()}`, { headers }),
          axios.get("/api/work-zones/", { headers }),
        ]);

        const savedLayout = layoutRes.data.lg || [];
        const activeDevices = devRes.data;

        const newLayout = activeDevices.map((dev, i) => {
          const saved = savedLayout.find((l) => l.i === dev.id.toString());
          if (saved) return saved;
          return {
            i: dev.id.toString(),
            x: (i * 4) % 12,
            y: Infinity,
            w: 4,
            h: 3,
            minW: 2,
            minH: 2,
          };
        });

        setDevices(activeDevices);
        setLayout(newLayout);
        setWorkZones(zonesRes.data);
      } catch (e) {
        toast.error("{i18n.t('multi_monitor_page.data_loading_error', 'Ошибка загрузки данных')}");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    const fetchLive = async () => {
      try {
        const token = localStorage.getItem("token");
        const devRes = await axios.get(`/api/fleet/realtime/?_t=${Date.now()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDevices(devRes.data);
      } catch (e) {
        console.error(e);
      }
    };

    const interval = setInterval(fetchLive, 5000);
    return () => clearInterval(interval);
  }, []);

  // Отслеживаем окончание первичной загрузки
  useEffect(() => {
    if (!loading && !initialLoadDone) {
      setInitialLoadDone(true);
    }
  }, [loading, initialLoadDone]);

  const onLayoutChange = (currentLayout) => {
    setLayout((prevLayout) => {
      const updatedLayout = [...prevLayout];
      currentLayout.forEach((currentItem) => {
        const index = updatedLayout.findIndex(
          (item) => item.i === currentItem.i,
        );
        if (index !== -1) {
          updatedLayout[index] = { ...updatedLayout[index], ...currentItem };
        } else {
          updatedLayout.push(currentItem);
        }
      });
      return updatedLayout;
    });
  };

  const handleSaveLayout = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "/api/user/layout/",
        { lg: layout },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Расположение камер сохранено!");
    } catch (e) {
      toast.error("{i18n.t('multi_monitor_page.save_error', 'Ошибка сохранения')}");
    } finally {
      setSaving(false);
    }
  };

  const displayedDevices = useMemo(() => {
    if (selectedZoneFilter === "") return devices;
    return devices.filter((d) => d.work_zone === parseInt(selectedZoneFilter));
  }, [devices, selectedZoneFilter]);

  const resetLayout = (forceRebuild = false) => {
    setGridSize("all");
    setGridCols(12);
    const defaultLayout = displayedDevices.map((dev, i) => {
      const existing = forceRebuild
        ? {}
        : layout.find((l) => l.i === dev.id.toString()) || {};
      return {
        ...existing,
        i: dev.id.toString(),
        x: (i * 4) % 12,
        y: Math.floor((i * 4) / 12) * 3,
        w: 4,
        h: 3,
        minW: 2,
        minH: 2,
      };
    });
    setLayout(defaultLayout);
  };

  const applyGridPreset = (preset, forceRebuild = false) => {
    setGridSize(preset);
    if (preset === "all") {
      resetLayout(forceRebuild);
      return;
    }

    let c = 1;
    if (preset === "1x1") c = 1;
    else if (preset === "2x2") c = 2;
    else if (preset === "3x3") c = 3;
    else if (preset === "4x4") c = 4;
    else if (preset === "5x5") c = 5;

    setGridCols(c);

    const screenWidth = window.innerWidth - 60;
    const cellWidth = screenWidth / c;
    const cellHeight = cellWidth / 1.77;
    const h = Math.max(2, Math.round(cellHeight / 100));

    // Если форсируем перестройку, отбрасываем старую сортировку по визуальному порядку
    const sortedDevices = forceRebuild
      ? [...displayedDevices]
      : [...displayedDevices].sort((a, b) => {
          const lA = layout.find((l) => l.i === a.id.toString());
          const lB = layout.find((l) => l.i === b.id.toString());
          const yA = lA ? lA.y : Infinity;
          const yB = lB ? lB.y : Infinity;
          if (yA !== yB) return yA - yB;
          const xA = lA ? lA.x : Infinity;
          const xB = lB ? lB.x : Infinity;
          return xA - xB;
        });

    const newLayout = sortedDevices.map((dev, index) => {
      const existing = forceRebuild
        ? {}
        : layout.find((l) => l.i === dev.id.toString()) || {};
      return {
        ...existing,
        i: dev.id.toString(),
        x: index % c,
        y: Math.floor(index / c) * h,
        w: 1,
        h: h,
        minW: 1,
        minH: 1,
      };
    });

    setLayout(newLayout);
  };

  // Эффект: Перестроить сетку с нуля при переключении участка (фильтра)
  useEffect(() => {
    if (!initialLoadDone) return;
    applyGridPreset(gridSize, true); // forceRebuild = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedZoneFilter]);

  const getVisibleDevices = () => {
    if (gridSize === "all") return displayedDevices;

    let limit = 1;
    if (gridSize === "1x1") limit = 1;
    if (gridSize === "2x2") limit = 4;
    if (gridSize === "3x3") limit = 9;
    if (gridSize === "4x4") limit = 16;
    if (gridSize === "5x5") limit = 25;

    const sortedDevices = [...displayedDevices].sort((a, b) => {
      const lA = layout.find((l) => l.i === a.id.toString());
      const lB = layout.find((l) => l.i === b.id.toString());
      const yA = lA ? lA.y : Infinity;
      const yB = lB ? lB.y : Infinity;
      if (yA !== yB) return yA - yB;
      const xA = lA ? lA.x : Infinity;
      const xB = lB ? lB.x : Infinity;
      return xA - xB;
    });

    return sortedDevices.slice(0, limit);
  };

  const visibleDevices = getVisibleDevices();

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <CSpinner color="primary" />
      </div>
    );

  return (
    <div className="bg-dark min-vh-100 p-3">
      <CContainer fluid>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 bg-secondary bg-opacity-25 p-2 rounded">
          <div className="d-flex align-items-center gap-3">
            <CButton color="light" as={Link} to="/dashboard" size="sm">
              <CIcon icon={cilArrowLeft} className="me-2" /> {i18n.t('multi_monitor_page.back', 'Назад')}
            </CButton>
            <h4 className="text-white mb-0 ms-2">{i18n.t('multi_monitor_page.multi_monitor', 'Мультимонитор')}</h4>
          </div>

          <div className="d-flex flex-wrap gap-2 mt-2 mt-md-0 align-items-center">
            {!isMapMode && (
              <>
                <CFormSelect
                  size="sm"
                  className="w-auto"
                  value={gridSize}
                  onChange={(e) => applyGridPreset(e.target.value)}
                >
                  <option value="all">{i18n.t('multi_monitor_page.all_cameras_free', 'Все камеры (Свободно)')}</option>
                  <option value="1x1">{i18n.t('multi_monitor_page.one_by_one_camera', '1x1 (1 камера)')}</option>
                  <option value="2x2">{i18n.t('multi_monitor_page.two_by_two_cameras', '2x2 (4 камеры)')}</option>
                  <option value="3x3">{i18n.t('multi_monitor_page.three_by_three_cameras', '3x3 (9 камер)')}</option>
                  <option value="4x4">{i18n.t('multi_monitor_page.four_by_four_cameras', '4x4 (16 камер)')}</option>
                  <option value="5x5">{i18n.t('multi_monitor_page.five_by_five_cameras', '5x5 (25 камер)')}</option>
                </CFormSelect>

                <CButton
                  color="warning"
                  size="sm"
                  onClick={() => resetLayout(true)}
                  className="me-2 text-white"
                  title="Вернуть свободный режим"
                >
                  <CIcon icon={cilReload} className="me-1" /> {i18n.t('multi_monitor_page.reset', 'Сброс')}
                </CButton>
              </>
            )}

            <CButton
              color={isMapMode ? "secondary" : "primary"}
              onClick={() => setIsMapMode(false)}
              size="sm"
            >
              <CIcon icon={cilGrid} className="me-1" /> {i18n.t('multi_monitor_page.cameras', 'Камеры')}
            </CButton>
            <CButton
              color={isMapMode ? "primary" : "secondary"}
              onClick={() => setIsMapMode(true)}
              size="sm"
            >
              <CIcon icon={cilMap} className="me-1" /> {i18n.t('multi_monitor_page.map', 'Карта')}
            </CButton>
            {!isMapMode && (
              <CButton
                color="success"
                size="sm"
                onClick={handleSaveLayout}
                disabled={saving}
              >
                {saving ? (
                  <CSpinner size="sm" />
                ) : (
                  <>
                    <CIcon icon={cilSave} className="me-1" /> {i18n.t('multi_monitor_page.save_grid', 'Сохранить сетку')}
                  </>
                )}
              </CButton>
            )}
          </div>
        </div>

        {workZones.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mb-3 justify-content-center">
            <CButton
              color={selectedZoneFilter === "" ? "primary" : "light"}
              className={`fw-bold rounded-pill border shadow-sm ${selectedZoneFilter === "" ? "text-white" : "text-dark"}`}
              onClick={() => setSelectedZoneFilter("")}
            >
              {i18n.t("multi_monitor_page.all_objects", "Все объекты")}{" "}
            </CButton>

            {workZones.map((zone) => {
              const count = devices.filter(
                (d) => d.work_zone === zone.id,
              ).length;
              const isActive = selectedZoneFilter === zone.id.toString();

              return (
                <CButton
                  key={zone.id}
                  color={isActive ? "primary" : "light"}
                  className={`fw-bold rounded-pill border shadow-sm ${isActive ? "text-white" : "text-dark"}`}
                  onClick={() => setSelectedZoneFilter(zone.id.toString())}
                >
                  <CIcon icon={cilMap} className="me-1 opacity-75" />
                  {zone.name} <span className="opacity-75">({count})</span>
                </CButton>
              );
            })}
          </div>
        )}

        {isMapMode && (
          <div
            style={{
              height: "calc(100vh - 100px)",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <YMaps query={{ lang: "ru_RU" }}>
              <Map
                defaultState={{ center: [55.751574, 37.573856], zoom: 4 }}
                width="100%"
                height="100%"
              >
                <Clusterer
                  options={{
                    preset: "islands#invertedNightClusterIcons",
                    groupByCoordinates: false,
                  }}
                >
                  {displayedDevices
                    .filter((d) => d.lat > 0 && d.lon > 0)
                    .map((dev) => (
                      <Placemark
                        key={`${dev.id}_${dev.lat}_${dev.lon}_${dev.timestamp}`}
                        geometry={[dev.lat, dev.lon]}
                        properties={{
                          iconCaption: `${dev.name} (${dev.timestamp ? dayjs(dev.timestamp).format("HH:mm") : "—"})`,
                          balloonContentBody: `
                            <strong>${dev.name}</strong><br/>
                            CPU: ${dev.cpu}°C<br/>
                            Батарея: ${dev.battery}%<br/>
                            Обновлено: ${dev.timestamp ? dayjs(dev.timestamp).format("DD.MM.YYYY HH:mm:ss") : "{i18n.t('multi_monitor_page.no_data', 'Нет данных')}"}<br/>
                            <a href="/camera/${dev.id}" target="_blank" style="display:block; margin-top:5px; color: blue; text-decoration: underline;">{i18n.t('multi_monitor_page.open_tech_panel', 'Открыть панель техники')}</a>
                          `,
                        }}
                        options={{ preset: "islands#blueAutoIcon" }}
                        modules={["geoObject.addon.balloon", "geoObject.addon.hint"]}
                        onDblClick={() =>
                          window.open(`/camera/${dev.id}`, "_blank")
                        }
                      />
                    ))}
                </Clusterer>
                <ZoomControl options={{ float: "right" }} />
                <TypeSelector options={{ float: "right" }} />
              </Map>
            </YMaps>
          </div>
        )}

        {!isMapMode && (
          <ResponsiveGridLayout
            key={gridSize}
            className="layout"
            layouts={{ lg: layout }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{
              lg: gridCols,
              md: gridCols,
              sm: gridCols,
              xs: gridCols,
              xxs: gridCols,
            }}
            rowHeight={100}
            onLayoutChange={onLayoutChange}
            isDraggable={true}
            isResizable={true}
            compactType="vertical"
          >
            {visibleDevices.map((dev) => (
              <div key={dev.id.toString()}>
                <div
                  className="react-grid-item w-100 h-100 bg-black"
                  onDoubleClick={() =>
                    window.open(`/camera/${dev.id}`, "_blank")
                  }
                  title="{i18n.t('multi_monitor_page.double_click_open_panel', 'Двойной клик откроет панель экскаватора')}"
                >
                  {dev.frame_url ? (
                    <img
                      src={`${dev.frame_url}?t=${Date.now()}`}
                      alt={dev.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <div className="no-signal">{i18n.t('multi_monitor_page.no_signal', 'Нет сигнала')}</div>
                  )}

                  {/* Оверлей сверху (Имя, Температура, Батарея) */}
                  <div className="monitor-overlay">
                    <div className="monitor-name">{dev.name}</div>
                    <div className="monitor-stats">
                      <div className="stat-item text-danger-light">
                        CPU: {dev.cpu}°C
                      </div>
                      <div className="stat-item text-success-light">
                        БАТ: {dev.battery}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </ResponsiveGridLayout>
        )}
      </CContainer>
    </div>
  );
};

export default MultiMonitorPage;